import { useCallback, useEffect, useState } from "react";
import { api, errMsg, MONTHS_IT, STATUS_META, DOW_IT, download, pad } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";

const YEARS = Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 4 + i);
const TOTALS = [["PRESENTE", "Lav."], ["FERIE", "Fer."], ["MALATTIA", "Mal."], ["INFORTUNIO", "Inf."],
  ["PERMESSO", "Perm."],
  ["ASSENTE", "Ass."], ["RIPOSO", "Rip."]];

export default function AccountantDetail() {
  const { user } = useAuth();
  const canExport = ["admin", "manager"].includes(user?.role);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [department, setDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [empStatus, setEmpStatus] = useState("active");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/daily-detail", { params: { year, month, department, status: empStatus } });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [year, month, department, empStatus]);

  useEffect(() => { load(); }, [load]);

  const days = Array.from({ length: data?.days || 0 }, (_, i) => i + 1);
  const isWeekend = (d) => [0, 6].includes(new Date(year, month - 1, d).getDay());

  return (
    <div data-testid="accountant-detail-page" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Riepilogo Giorno per Giorno</h1>
          <p className="text-sm text-muted-foreground mt-1 tabular-nums">
            Dettaglio per il commercialista · {MONTHS_IT[month - 1]} {year} · {data?.rows?.length ?? 0} dipendenti
          </p>
        </div>
        {canExport && (
          <div className="flex gap-2">
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} variant={f === "xlsx" ? "default" : "outline"} size="sm" className="rounded-sm font-semibold"
                data-testid={`export-detail-${f}`}
                onClick={() => download(`/export/accountant-detail?year=${year}&month=${month}&fmt=${f}&department=${department}&status=${empStatus}`,
                  `riepilogo_giornaliero_${year}_${pad(month)}.${f}`).catch((e) => toast.error(errMsg(e)))}>
                <DownloadSimple size={14} className="mr-1.5" /> {f === "xlsx" ? "EXCEL" : f.toUpperCase()}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card p-3 flex flex-wrap gap-2">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-9 w-[140px] rounded-sm" data-testid="detail-month"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTHS_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-9 w-[100px] rounded-sm" data-testid="detail-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="detail-department"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i reparti</SelectItem>
            {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={empStatus} onValueChange={setEmpStatus}>
          <SelectTrigger className="h-9 w-[190px] rounded-sm" data-testid="detail-emp-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Solo dipendenti attivi</SelectItem>
            <SelectItem value="inactive">Solo non attivi</SelectItem>
            <SelectItem value="all">Attivi e non attivi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-auto scroll-thin" style={{ maxHeight: "72vh" }}>
        <table className="border-collapse text-sm" data-testid="detail-table">
          <thead className="sticky top-0 z-10">
            <tr className="bg-secondary">
              <th className="sticky-col bg-secondary border-b border-r border-border px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground min-w-[200px]">
                Dipendente
              </th>
              {days.map((d) => (
                <th key={d} className={`border-b border-r border-border w-[28px] py-1 text-center text-[11px] font-semibold tabular-nums ${isWeekend(d) ? "bg-muted text-muted-foreground" : ""}`}>
                  <div>{d}</div>
                  <div className="text-[9px] font-normal opacity-60">{DOW_IT[new Date(year, month - 1, d).getDay()]}</div>
                </th>
              ))}
              {TOTALS.map(([k, l]) => (
                <th key={k} className="border-b border-r border-border px-1.5 py-1.5 text-right text-[10px] font-semibold uppercase text-muted-foreground whitespace-nowrap">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={days.length + 8} className="px-3 py-8 text-center text-muted-foreground">Caricamento...</td></tr>}
            {!loading && data?.rows?.length === 0 && (
              <tr><td colSpan={days.length + 8} className="px-3 py-8 text-center text-muted-foreground" data-testid="detail-empty">Nessun dato</td></tr>
            )}
            {!loading && (data?.rows || []).map((r) => (
              <tr key={r.employee_id} className="hover:bg-secondary/40" data-testid={`detail-row-${r.code}`}>
                <td className="sticky-col border-b border-r border-border px-2 py-1">
                  <div className="text-[13px] font-semibold leading-tight">{r.last_name} {r.first_name}</div>
                  <div className="text-[10px] text-muted-foreground tabular-nums">{r.code} · {r.department}</div>
                </td>
                {days.map((d) => {
                  const cell = r.days[d] || r.days[String(d)];
                  const meta = cell?.status ? STATUS_META[cell.status] : null;
                  return (
                    <td key={d} className={`border-b border-r border-border p-0 text-center ${isWeekend(d) ? "bg-muted/40" : ""}`}>
                      <div title={cell ? cell.status : ""}
                        className={`h-7 w-[28px] flex items-center justify-center text-[10px] font-bold ${meta ? meta.cell : ""}`}>
                        {cell?.abbr || ""}
                      </div>
                    </td>
                  );
                })}
                {TOTALS.map(([k]) => (
                  <td key={k} className="border-b border-r border-border px-1.5 py-1 text-right text-[12px] font-semibold tabular-nums">
                    {r[k] ?? 0}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-3 text-[11px]">
        {Object.keys(STATUS_META).map((s) => (
          <span key={s} className="flex items-center gap-1.5 font-medium text-muted-foreground">
            <span className={`h-3 w-5 rounded-sm border border-border ${STATUS_META[s].cell}`} /> {s} ({STATUS_META[s].short})
          </span>
        ))}
      </div>
    </div>
  );
}
